import strings from "global/constants/StringConstants";
import { RequestParticipant, SignRequest } from "models/interfaces";
import { splitByCamelCase } from "helpers/methods";
import { getProgress } from "./MySignRequestHelper";
import signRequestsTokens, {
  signRequestsStatusTokens,
  SignRequestsStatusKey,
} from "./SignRequests.tokens";
import {
  getRequestUiStatus,
  SIGN_REQUESTS_STATUS_OPTIONS,
} from "./SignRequestsFiltersHelper";

/**
 * Chip treatment for each `UiSignStatus` the filtered list sends. "Sent" wears
 * the amber the screen already uses for a request nobody has signed.
 */
const UI_STATUS_KEYS: Record<string, SignRequestsStatusKey> = {
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  SENT: "pending",
  IN_PROGRESS: "progress",
  AWAITING_APPROVAL: "approval",
  COMPLETED: "completed",
  DECLINED: "declined",
  EXPIRED: "expired",
  VOIDED: "voided",
};

/** Chip colours for a `UiSignStatus`, for the Status filter's own chips. */
export const getUiStatusTone = (uiStatus: string) =>
  signRequestsStatusTokens[UI_STATUS_KEYS[uiStatus] ?? "voided"];

export interface SignRequestsRowStatus {
  key: SignRequestsStatusKey;
  label: string;
  /** Draft / closed rows dim their document title in the design. */
  muted: boolean;
  /** 0–100, drives the progress bar fill. */
  percent: number;
  /** "2/3", "Not sent" or "—" depending on the state. */
  note: string;
}

/**
 * Maps an app `SignRequest` onto the design's status vocabulary.
 *
 * The design splits the app's single `Pending` status in two — "Pending" while
 * nobody has signed and "In progress" once someone has — so the signer tally
 * decides which chip a pending request gets. Statuses the design has no chip
 * for (Scheduled, In Review, …) fall back to the neutral treatment but keep
 * their own label so no information is lost.
 *
 * A row from the filtered list arrives with the backend's own `uiStatus`, and
 * that wins: the chip then always agrees with the Status filter.
 */
export const getSignRequestsRowStatus = (
  request: SignRequest,
): SignRequestsRowStatus => {
  const signers: RequestParticipant[] = request.signers ?? [];
  const { progress, completedCount, totalCount } = getProgress(signers);

  const uiStatus = getRequestUiStatus(request);
  const uiKey = uiStatus ? UI_STATUS_KEYS[uiStatus] : undefined;

  const rawKey: SignRequestsStatusKey = (() => {
    switch (request.status) {
      case strings.draft:
        return "draft";
      case strings.signed:
      case strings.completed:
        return "completed";
      case strings.declined:
        return "declined";
      case strings.rejected:
        return "rejected";
      case strings.expired:
        return "expired";
      case strings.voided:
        return "voided";
      case strings.pending:
      case strings.inReview:
      case strings.scheduled:
        return completedCount > 0 ? "progress" : "pending";
      default:
        return "voided";
    }
  })();

  const known = [
    strings.draft,
    strings.signed,
    strings.completed,
    strings.declined,
    strings.rejected,
    strings.expired,
    strings.voided,
    strings.pending,
  ].includes(request.status);

  const { status } = signRequestsTokens;
  const isDraft = request.status === strings.draft;
  const key = uiKey ?? rawKey;
  const uiLabel = uiKey
    ? SIGN_REQUESTS_STATUS_OPTIONS.find((option) => option.value === uiStatus)
        ?.label
    : undefined;

  return {
    key,
    label:
      uiLabel ??
      (known
        ? signRequestsStatusTokens[key].label
        : splitByCamelCase(request.status)),
    muted: signRequestsStatusTokens[key].muted,
    percent: isDraft ? 0 : Math.round(progress),
    note: isDraft
      ? status.draftNote
      : totalCount
        ? `${completedCount}/${totalCount}`
        : status.emptyNote,
  };
};

/** "DP" from "Devarsh Panchal" — at most two letters, as in the design. */
export const getSignerInitials = (name: string) =>
  (name ?? "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? "")
    .join("")
    .toUpperCase();

/**
 * How a signer should be named in the avatar stack, its tooltip and the
 * recipient lists.
 *
 * An in-person signer is created without a name or an email — the walk-in
 * signer's details are captured on the host's device when the session starts —
 * so `signer.name` is blank until then and every label built from it alone
 * degraded to a bare dash ("— Pending" in the row tooltip). Fall through to the
 * email, then to the delivery mode, so the row always says *who* is pending, or
 * failing that *how* they were invited.
 */
export const getSignerLabel = (signer: RequestParticipant): string => {
  const name = (signer?.name ?? "").trim();
  if (name) return name;

  const email = (signer?.email ?? "").trim();
  if (email) return email;

  return signer?.deliveryMode === strings.deliveryModes.inPerson
    ? strings.inPerson.unnamedSignerLabel
    : "Unnamed signer";
};

/**
 * The avatar tooltip: who, their status, and — for an in-person signer, whose
 * name nobody has yet — how they are going to sign, which is the only useful
 * thing left to say about them.
 */
export const getSignerTooltip = (signer: RequestParticipant): string => {
  const status = (signer?.status ?? "").trim();
  const parts = [getSignerLabel(signer)];
  if (status) parts.push(splitByCamelCase(status));

  const line = parts.join(" — ");
  return signer?.deliveryMode === strings.deliveryModes.inPerson
    ? `${line}\n${strings.inPerson.unnamedSignerHint}`
    : line;
};
